/**
 * Drawing background.
 *
 * Owned by the shell rather than the renderer so that the container can paint it
 * immediately — the renderer arrives in a lazily-loaded chunk, and without this the
 * viewer would flash the page background while that chunk resolves.
 */
export const DXF_CANVAS_BACKGROUND = '#1e1e1e';

/**
 * Color a picked item takes.
 */
export const SELECTION_ACCENT_COLOR = '#2f80ed';

/**
 * Color geometry takes while the pointer is over it.
 */
export const HOVER_ACCENT_COLOR = '#d8d8d8';

/**
 * Accent stroke widths, in CSS pixels.
 *
 * Wider than any lineweight the drawing itself uses, so an accent reads as a halo
 * around the stroke.
 */
export const SELECTION_ACCENT_WIDTH_PX = 4;
export const HOVER_ACCENT_WIDTH_PX = 3;

/**
 * Color an item takes while a marquee is poised to collect it.
 */
export const PREVIEW_ACCENT_COLOR = '#7fb3f2';

/** Preview color while the box is subtracting. */

export const PREVIEW_ACCENT_WIDTH_PX = 3;

/**
 * Stroke width of a highlighted entity while viewing interrogation results.
 */
export const HIGHLIGHT_ACCENT_WIDTH_PX = 2;

/**
 * A highlighted gap span: the item's colour blended this far toward the canvas
 * background, dashed, so the gap reads as a measurement rather than geometry.
 */
export const HIGHLIGHT_SPAN_DIM = 0.4;
export const HIGHLIGHT_SPAN_DASH: readonly [number, number] = [6, 4];

/**
 * Line colour while viewing interrogation results: every line white against
 * the dark canvas, so only the highlights carry colour. Lines mapped as ignored
 * take the mapping's own ignore grey instead — see `interrogationLines`.
 */
export const INTERROGATION_LINE_COLOR = '#ffffff';
export const INTERROGATION_LINE_WIDTH_PX = 1;

/**
 * Pick radius in CSS pixels.
 *
 * A screen distance rather than a world one, converted through the current zoom at
 * pick time, so hitting a line feels identical at every zoom level.
 */
export const PICK_TOLERANCE_PX = 6;

/**
 * How far focusing an interrogation item may zoom in, as a multiple of fitting
 * the whole drawing.
 *
 * The CAD viewer's feature snap uses the same limit (`MAX_SNAP_ZOOM`): a small
 * feature is brought forward, and the drawing around it stays on screen.
 */
export const FEATURE_SNAP_MAX_ZOOM = 8;

/**
 * Longest a feature focus takes to pan and zoom into place.
 *
 * The CAD viewer's feature snap uses this same duration
 * (`TWEEN_TIMES.FEATURE_SNAP_TO`). A move that crosses the view uses it; a
 * shorter move takes less, down to `FEATURE_SNAP_MIN_DURATION_MS`.
 */
export const FEATURE_SNAP_DURATION_MS = 750;

/** Shortest a feature focus takes. Closer callouts stay a lerp, and stop here. */
export const FEATURE_SNAP_MIN_DURATION_MS = 250;

/**
 * How far outside its drawn border a marquee still collects, in CSS pixels.
 */
export const MARQUEE_TOLERANCE_PX = 3;

/**
 * Snap radius in CSS pixels, for the measure tool.
 */
export const SNAP_TOLERANCE_PX = 12;

/** Marquee preview. Solid encloses, dashed crosses; subtracting restyles. */
/**
 * Overlap ("enclosed") selection — the left-to-right box that takes only what it
 * fully surrounds.
 */
export const MARQUEE_STROKE_COLOR = '#2f80ed';
export const MARQUEE_FILL_COLOR = 'rgba(47, 128, 237, 0.12)';

/**
 * Contact ("crossing") selection — the right-to-left box that takes anything it
 * touches.
 */
export const MARQUEE_CROSSING_STROKE_COLOR = '#27a065';
export const MARQUEE_CROSSING_FILL_COLOR = 'rgba(39, 160, 101, 0.12)';

/** Measure tool. */
export const MEASURE_COLOR = '#ffd166';
export const SNAP_RING_RADIUS_PX = 6;

/** Axis-aligned dimensions, color-coded per axis. */
export const DIMENSION_X_COLOR = '#e56b6f';
export const DIMENSION_Y_COLOR = '#6bcf8f';
/** Optimal bounding box outline. */
export const OPTIMAL_BOX_COLOR = '#9b8cf0';

/** Color the drawing takes when layer colouring is switched off. */
export const MONOCHROME_COLOR = '#d8d8d8';

/**
 * DOM text-layer color (CSS overlay, not WebGL).
 */
export const TEXT_LAYER_COLOR = 'rgba(255, 255, 255, 0.92)';

/** Highlight behind selected text in the DOM text layer. */
export const TEXT_SELECTION_BACKGROUND = 'rgba(66, 153, 225, 0.55)';

/**
 * Class on the DOM text layer. The input controller uses it to tell a press on
 * a label from a press on the canvas, so it is shared rather than repeated.
 */
export const DXF_TEXT_LAYER_CLASS = 'dxf-text-layer';

/**
 * Custom property the input controller sets on the surface while a press would
 * pan, overriding a selectable label's own I-beam (`text`, or `vertical-text` for
 * labels running up the screen). A label's cursor would otherwise beat the
 * surface's `grab` while Space is held, hiding that a press will pan. Unset the
 * rest of the time, so each label falls back to its own.
 */
export const DXF_TEXT_CURSOR_VAR = '--dxf-text-cursor';

/**
 * How many states the mapping undo stack keeps, including the present one.
 */
export const MAPPING_HISTORY_CAPACITY = 50;

/**
 * Rotate-control chip colours — match partviewer's `DxfRotateButton` so the
 * legacy DXF path and this viewer share the same chrome.
 */
export const ROTATE_CHIP_BG = 'rgba(41, 41, 41, 0.75)';
export const ROTATE_CHIP_BG_HOVER = 'rgba(41, 41, 41, 0.85)';

/**
 * Stacking bands inside the DXF viewer surface (and chrome that overlays the drawing).

 */
export const DXF_Z_INDEX = {
  /** WebGL canvas (explicit so scene overlays have something to sit above). */
  CANVAS: 0,
  /** DOM text layer + non-interactive SVG tool overlays (marquee, measure, …). */
  SCENE_OVERLAY: 1,
  /** Persistent chrome: rotate chips, details readout, side panels. */
  CHROME: 2,
  /**
   * Transient UI over the drawing: mapping flyout, context menu, brush badge.
   * Must beat scene overlays even when those mount later in the DOM.
   */
  FLOATING: 3,
  /** Full-bleed load / error covers. */
  STATUS: 4,
} as const;

/**
 * Container query under which the side-panel column must sit below the toolbar.
 *
 * The toolbar is centred in the viewer, so the two collide once the viewer is
 * narrower than the toolbar plus the panel column on both sides. Mapping mode's
 * toolbar is the wider one (brushes, undo/redo, save), hence the larger breakpoint.
 * Widen these when a button is added to either toolbar.
 */
export const dxfSidePanelsBelowToolbarQuery = (mode: string): string =>
  `@container viewer-content (max-width: ${
    mode === 'mapping' ? '1160px' : '860px'
  })`;
